import { assertTms, newRequestId } from './tmsCommerceClient';
import { createTmsMediaBridge } from './tmsMediaBridge';

const SMALL_FILE_BYTES = 16 * 1024 * 1024;
const PREVIEW_MIME = new Set(['video/mp4', 'video/webm', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg',
  'application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif', 'text/plain', 'text/vtt']);
export function createContentDelivery({ call, check: assertCurrent }) {
  const deliveries = new Map(), releases = new Set(), requests = new Set(); let disposed = false;
  const check = () => { assertTms(!disposed); assertCurrent(); };
  const approvedUrl = value => { const url = new URL(value); assertTms(url.protocol === 'https:' && !url.username && !url.password); return url.href; };
  async function delivery(file, accessOperation, disposition, key) {
    check();
    const body = { grantId: file.grantId, resource: file.resourceRef, accessOperation, disposition }, binding = JSON.stringify(body);
    let attempt = deliveries.get(key);
    if (!attempt) { attempt = { binding, body: JSON.parse(binding), key, receipt: null, flight: null }; deliveries.set(key, attempt); }
    assertTms(attempt.binding === binding);
    if (attempt.flight) return attempt.flight;
    if (attempt.receipt && Date.parse(attempt.receipt.expiresAt) > Date.now()) return attempt.receipt;
    if (attempt.receipt) { attempt.key = newRequestId(); attempt.receipt = null; }
    const promise = (async () => {
      const result = await call('website/access/issue', { ...attempt.body, idempotencyKey: attempt.key }); check();
      assertTms(typeof result.deliveryId === 'string' && ['SIGNED_URL', 'AUTHORITY_PROXY'].includes(result.mode) && result.completionProven === false
        && typeof result.mimeType === 'string' && typeof result.immediateRevocation === 'boolean'
        && Number.isSafeInteger(result.sizeBytes) && result.sizeBytes > 0 && Number.isFinite(Date.parse(result.expiresAt)));
      attempt.receipt = result;
      if (Date.parse(result.expiresAt) <= Date.now()) throw new Error('The confirmed delivery expired. Retry for current access.');
      return result;
    })().catch(error => {
      // Only this explicit owner rejection permits the NEXT user attempt to
      // prepare a new access ticket. Network/unknown outcomes keep the key.
      // Replacing a ticket neither completes bytes nor changes an entitlement.
      if (error?.code === 'CONTENT_DELIVERY_REPLAY_CHANGED_OR_EXPIRED') {
        attempt.key = newRequestId(); attempt.receipt = null;
        throw new Error('The previous delivery ticket is no longer current. Select the action again to prepare current access. No completed transfer is assumed.');
      }
      throw error;
    }).finally(() => { if (attempt.flight === promise) attempt.flight = null; });
    attempt.flight = promise; return promise;
  }
  function track(release) {
    const stop = () => { releases.delete(stop); release(); };
    releases.add(stop); return stop;
  }
  async function proxy(file, receipt, operation, range, signal) {
    const response = await call('files/delivery/read', { grantId: file.grantId, accessOperation: operation,
      deliveryId: receipt.deliveryId, range }, { raw: true, signal });
    const transferId = response.headers.get('X-Content-Transfer-Id'), length = Number(response.headers.get('X-Content-Size-Bytes'));
    const selected = range && /^bytes=(\d+)-(\d+)$/.exec(range);
    const expected = selected ? Number(selected[2]) - Number(selected[1]) + 1 : receipt.sizeBytes;
    try {
      check(); assertTms(response.body && response.status === (range ? 206 : 200) && Number.isSafeInteger(length) && length === expected
        && /^[A-Za-z0-9_-]{1,160}$/.test(transferId || '') && response.headers.get('Content-Type')?.split(';')[0] === receipt.mimeType);
      if (range) assertTms(selected && response.headers.get('Content-Range') === `bytes ${selected[1]}-${selected[2]}/${receipt.sizeBytes}`);
      return { response, expected, transferId };
    } catch (error) { await response.body?.cancel().catch(() => {}); throw error; }
  }
  // Success is emitted after the owner's chunked EOF and exact byte count. A
  // delivery ticket or native player's ended event is not a transfer receipt.
  async function consume(response, expected, sink, maximum = Number.MAX_SAFE_INTEGER) {
    check(); assertTms(response.body); const reader = response.body.getReader(); let bytes = 0;
    try {
      for (;;) {
        check(); const chunk = await reader.read(); check(); if (chunk.done) break;
        bytes += chunk.value.byteLength;
        assertTms(bytes <= expected && bytes <= maximum); await sink.write(chunk.value); check();
      }
      assertTms(bytes === expected); await sink.close(); check();
    } catch (error) { await sink.abort(error).catch(() => {}); throw error; }
    finally { await reader.cancel().catch(() => {}); }
  }
  async function blob(response, expected, mimeType) {
    const chunks = [];
    await consume(response, expected, { write: async bytes => { chunks.push(bytes); }, close: async () => {}, abort: async () => { chunks.length = 0; } }, SMALL_FILE_BYTES);
    return new Blob(chunks, { type: mimeType });
  }
  async function save(file, operation, key) {
    check();
    const filename = file.title.replace(/[<>:"/\\|?*]/g, '_');
    const handle = window.showSaveFilePicker ? await window.showSaveFilePicker({ suggestedName: filename }) : null;
    check(); const receipt = await delivery(file, operation, 'attachment', key); check();
    if (!handle && receipt.sizeBytes > SMALL_FILE_BYTES) throw new Error('Saving this file requires a desktop browser with secure file saving support. No file transfer started.');
    const controller = new AbortController(); requests.add(controller);
    try {
      let result;
      if (receipt.mode === 'AUTHORITY_PROXY') result = await proxy(file, receipt, operation, null, controller.signal);
      else {
        const response = await fetch(approvedUrl(receipt.url), { credentials: 'omit', redirect: 'error', cache: 'no-store', signal: controller.signal });
        if (!response.ok) { await response.body?.cancel().catch(() => {}); throw new Error('The current file could not be downloaded.'); }
        result = { response, expected: receipt.sizeBytes, transferId: null };
      }
      if (handle) {
        let writer;
        try { check(); writer = await handle.createWritable(); }
        catch (error) { await result.response.body?.cancel().catch(() => {}); throw error; }
        await consume(result.response, result.expected, writer);
      } else {
        const contents = await blob(result.response, result.expected, receipt.mimeType), url = URL.createObjectURL(contents), link = document.createElement('a');
        const release = track(() => URL.revokeObjectURL(url)); check(); link.href = url; link.download = filename; link.click(); setTimeout(release, 10000);
      }
      check(); return { transferId: result.transferId };
    } finally { controller.abort(); requests.delete(controller); }
  }
  async function preview(file, key = newRequestId(), onFullTransfer) {
    const receipt = await delivery(file, 'ACADEMY_READ', 'inline', key); check();
    if (!PREVIEW_MIME.has(receipt.mimeType)) throw new Error('This file type must be downloaded; it cannot execute inside this account.');
    if (receipt.mode === 'SIGNED_URL' && receipt.mimeType !== 'text/vtt') return { ...receipt, previewUrl: approvedUrl(receipt.url), transferId: null, localBlob: false, release: () => {} };
    if (/^(audio|video)\//.test(receipt.mimeType)) {
      let active = true;
      const bridge = await createTmsMediaBridge(async signal => {
        check(); const current = await delivery(file, 'ACADEMY_READ', 'inline', key); check();
        assertTms(current.mode === 'AUTHORITY_PROXY' && current.sizeBytes === receipt.sizeBytes && current.mimeType === receipt.mimeType);
        return { mimeType: current.mimeType, sizeBytes: current.sizeBytes,
          async stream(range, sink, requestSignal) {
            const selected = await proxy(file, current, 'ACADEMY_READ', range, requestSignal || signal);
            await consume(selected.response, selected.expected, sink.getWriter());
            check();
            // Only one complete owner stream can supply this receipt. Native
            // partial ranges are never accumulated into a completion claim.
            if (active && range === null && selected.expected === current.sizeBytes) onFullTransfer?.({
              grantId: file.grantId, resourceRef: { ...file.resourceRef }, transferId: selected.transferId,
            });
          } };
      }, check);
      try { check(); } catch (error) { bridge.release(); throw error; }
      const release = track(() => { active = false; onFullTransfer = null; bridge.release(); });
      return { ...receipt, previewUrl: bridge.url, transferId: null, localBlob: false, release };
    }
    if (receipt.sizeBytes > SMALL_FILE_BYTES) throw new Error('This document is too large for an in-memory preview. Save the lesson file.');
    const controller = new AbortController(); requests.add(controller);
    try {
      let result;
      if (receipt.mode === 'SIGNED_URL') {
        const response = await fetch(approvedUrl(receipt.url), { credentials: 'omit', redirect: 'error', cache: 'no-store', signal: controller.signal }); check();
        if (!response.ok) { await response.body?.cancel().catch(() => {}); throw new Error('The selected captions are unavailable.'); }
        result = { response, expected: receipt.sizeBytes, transferId: null };
      } else result = await proxy(file, receipt, 'ACADEMY_READ', null, controller.signal);
      const contents = await blob(result.response, result.expected, receipt.mimeType); check();
      const url = URL.createObjectURL(contents), release = track(() => URL.revokeObjectURL(url));
      return { ...receipt, previewUrl: url, transferId: result.transferId, localBlob: true, release };
    } finally { controller.abort(); requests.delete(controller); }
  }
  return { preview, download: (file, key = newRequestId()) => save(file, 'CONTENT_DOWNLOAD', key),
    saveLesson: (file, key = newRequestId()) => save(file, 'ACADEMY_READ', key),
    release() { disposed = true; releases.forEach(stop => stop()); requests.forEach(controller => controller.abort()); requests.clear(); deliveries.clear(); } };
}
